import {
  searchPeople,
  uniqueAutoMatch,
} from "./bowlerMatch";
import { normalizeUsbcId } from "./bowlerMatchKeys";
import {
  highestQualifyingAverage,
  uniqueAutoCatalogMatch,
} from "./catalogAverageMatch";

const catalogHitsToSuggestions = (catalogHits, minGamesForAverage) =>
  (catalogHits || [])
    .map((hit) => {
      const person = hit?.person;
      if (!person?.id) return null;
      const candidate = highestQualifyingAverage(
        person.averages,
        minGamesForAverage
      );
      return {
        extractedBowlerID: person.id,
        name: person.name,
        usbcId: person.usbcId || person.usbcID,
        status: hit.status,
        reason: hit.reason,
        average: hit.average ?? candidate?.average ?? null,
        games: candidate?.games ?? null,
      };
    })
    .filter(Boolean);

export const matchSignupRow = ({
  name,
  usbcID,
  email,
  phone,
  writtenAverage,
  orgBowlers = [],
  catalogPeople = [],
  minGamesForAverage = 21,
  excludeOrgIds = [],
  excludeCatalogIds = [],
}) => {
  const orgHits = searchPeople({
    query: name,
    usbcID,
    email,
    phone,
    writtenAverage,
    people: orgBowlers,
    usbcOf: (bowler) => bowler.usbcID || bowler.usbcId,
    averageOf: (bowler) =>
      bowler.average == null || bowler.average === ""
        ? null
        : Number(bowler.average),
    excludeIds: excludeOrgIds,
  });
  const catalogHits = searchPeople({
    query: name,
    usbcID,
    email,
    phone,
    writtenAverage,
    people: catalogPeople,
    usbcOf: (person) => person.usbcId || person.usbcID,
    averageOf: (person) =>
      highestQualifyingAverage(person.averages, minGamesForAverage)?.average ??
      null,
    excludeIds: excludeCatalogIds,
  });

  const orgAuto = uniqueAutoMatch(orgHits);
  const catalogAutoHit = uniqueAutoMatch(catalogHits);
  const catalogUnique = uniqueAutoCatalogMatch(
    catalogHitsToSuggestions(catalogHits, minGamesForAverage),
    { name, usbcID }
  );
  const catalogAuto = catalogAutoHit
    ? {
        ...catalogAutoHit,
        average: catalogAutoHit.average ?? catalogUnique?.average ?? null,
      }
    : catalogUnique
      ? {
          person: {
            id: catalogUnique.extractedBowlerID,
            name: catalogUnique.name,
            usbcId: catalogUnique.usbcId,
            usbcID: catalogUnique.usbcId,
            averages: catalogHits.find(
              (hit) => hit.person?.id === catalogUnique.extractedBowlerID
            )?.person?.averages,
          },
          average: catalogUnique.average,
          status: "autoMatched",
          reason: catalogUnique.reason || "exactName",
        }
      : null;
  const padUsbc = normalizeUsbcId(usbcID);
  const orgUsbc = normalizeUsbcId(
    orgAuto?.person?.usbcID || orgAuto?.person?.usbcId
  );
  const catalogAverage = catalogAuto?.average ?? null;
  const written =
    writtenAverage == null || writtenAverage === ""
      ? null
      : Number(writtenAverage);
  const writtenNum = Number.isFinite(written) ? written : null;

  return {
    org: {
      auto: orgAuto?.person || null,
      reason: orgAuto?.reason || null,
      suggestions: orgHits,
      usbcConflict: Boolean(
        orgAuto && padUsbc && orgUsbc && padUsbc !== orgUsbc
      ),
    },
    catalog: {
      auto: catalogAuto
        ? {
            ...catalogAuto.person,
            average: catalogAverage,
            status: "autoMatched",
            reason: catalogAuto.reason,
          }
        : null,
      reason: catalogAuto?.reason || null,
      suggestions: catalogHits,
      averageDisagrees: Boolean(
        writtenNum != null &&
          catalogAverage != null &&
          Number(catalogAverage) !== writtenNum
      ),
    },
  };
};

export const signupRowToCreateTeamBowler = ({
  name,
  usbcID,
  email,
  phone,
  writtenAverage,
  ticks = {},
  match,
}) => {
  const org = match?.org?.auto;
  const catalog = match?.catalog?.auto;
  const padUsbc = normalizeUsbcId(usbcID);
  const orgUsbc = normalizeUsbcId(org?.usbcID || org?.usbcId);
  const catalogUsbc = normalizeUsbcId(catalog?.usbcId || catalog?.usbcID);
  let usbc = padUsbc;
  if (match?.org?.usbcConflict) {
    usbc = orgUsbc;
  } else if (!usbc) {
    usbc = orgUsbc || catalogUsbc;
  }

  const written =
    writtenAverage == null || writtenAverage === ""
      ? null
      : Number(writtenAverage);
  const hasWritten = Number.isFinite(written);
  const catalogAverage =
    catalog?.average == null ? null : Number(catalog.average);
  let average = "";
  let hasAverage = false;
  if (hasWritten) {
    average = written;
    hasAverage = true;
  } else if (catalogAverage != null) {
    average = catalogAverage;
    hasAverage = true;
  }

  return {
    name: org?.name || catalog?.name || name || "",
    usbcID: usbc || "",
    email: org?.email || email || "",
    phone: org?.phone || phone || "",
    average: hasAverage ? average : "",
    hasAverage,
    existingBowler: Boolean(org),
    id: org?.id || null,
    catalogMatch: catalog
      ? {
          extractedBowlerID: catalog.id,
          name: catalog.name,
          usbcId: catalog.usbcId || catalog.usbcID,
          status: "autoMatched",
          reason: catalog.reason,
          average: catalog.average,
        }
      : null,
    usbcConflict: Boolean(match?.org?.usbcConflict),
    averageDisagrees: Boolean(match?.catalog?.averageDisagrees),
    selectedSidePots: ticks,
  };
};
